<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { ROUTES } from '../../core/config/constants.js'
import { submitB2BForm } from '../../core/services/b2bForms.js'
import { callFunction } from '../../core/firebase/functions.js'
import { recordVisit } from '../../core/services/joinPageVisits.js'
import { fetchCities, fetchStreets } from '../../core/services/israelAddresses.js'
import SearchPickerField from '../components/join/SearchPickerField.vue'

// Form state
const formData = ref({
  city: null,
  biz_name: null,
  vat: null,
  address: null,
  contact: null,
  phone: null,
  email: null,
  supply: null,
  tos: null,
  marketing: false,
})

// Delivery restrictions state
const hasDeliveryRestrictions = ref(false)
const selectedDays = ref([])
const selectedTimeSlots = ref([])

const deliveryDays = [
  { key: 'sunday', label: "א'" },
  { key: 'monday', label: "ב'" },
  { key: 'tuesday', label: "ג'" },
  { key: 'wednesday', label: "ד'" },
  { key: 'thursday', label: "ה'" },
]

const deliveryTimeSlots = [
  { key: '8-14', label: '08:00 עד 14:00' },
  { key: '8-16', label: '08:00 עד 16:00' },
  { key: '6-14', label: '06:00 עד 14:00' },
  { key: '6-16', label: '06:00 עד 16:00' },
]

// Toggle day selection
const toggleDay = (dayKey) => {
  const index = selectedDays.value.indexOf(dayKey)
  if (index === -1) {
    selectedDays.value.push(dayKey)
  } else {
    selectedDays.value.splice(index, 1)
  }
  updateSupplyString()
}

// Toggle time slot selection
const toggleTimeSlot = (slotKey) => {
  const index = selectedTimeSlots.value.indexOf(slotKey)
  if (index === -1) {
    selectedTimeSlots.value.push(slotKey)
  } else {
    selectedTimeSlots.value.splice(index, 1)
  }
  updateSupplyString()
}

// Update the supply string based on selections
const updateSupplyString = () => {
  if (!hasDeliveryRestrictions.value) {
    formData.value.supply = ''
    return
  }

  if (selectedDays.value.length === 0 || selectedTimeSlots.value.length === 0) {
    formData.value.supply = ''
    return
  }

  // Build days string in order
  const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday']
  const sortedDays = dayOrder.filter((d) => selectedDays.value.includes(d))
  const dayLabels = sortedDays.map((d) => deliveryDays.find((day) => day.key === d)?.label)
  const daysStr = dayLabels.join(' ')

  // Build time slots string
  const timeLabels = selectedTimeSlots.value.map(
    (s) => deliveryTimeSlots.find((slot) => slot.key === s)?.label
  )
  const timesStr = timeLabels.join(', ')

  formData.value.supply = `ימי ושעות קבלת סחורה: בימים ${daysStr} ובשעות ${timesStr}`
}

// Watch for toggle changes
watch(hasDeliveryRestrictions, (newVal) => {
  if (!newVal) {
    selectedDays.value = []
    selectedTimeSlots.value = []
    formData.value.supply = ''
  }
})

// Validation state
const validationError = ref('')
const hasAttemptedSubmit = ref(false)

// Validation functions
const validators = {
  city: (value) => {
    if (!value || !String(value).trim()) return 'יש לבחור או להקליד עיר'
    return ''
  },
  biz_name: (value) => {
    if (!value || !value.trim()) return 'יש להזין שם עסק'
    if (value.trim().length < 2) return 'שם העסק חייב להכיל לפחות 2 תווים'
    return ''
  },
  vat: (value) => {
    if (!value || !value.trim()) return 'יש להזין מספר ח.פ / עוסק מורשה'
    const cleaned = value.replace(/\D/g, '')
    if (cleaned.length < 8 || cleaned.length > 9)
      return 'מספר ח.פ / עוסק מורשה חייב להכיל 8-9 ספרות'
    return ''
  },
  address: (_value, context) => {
    if (!context?.selectedStreet) return 'יש לבחור או להקליד רחוב'
    if (!context?.houseNumber?.trim()) return 'יש להזין מספר בית'
    return ''
  },
  contact: (value) => {
    if (!value || !value.trim()) return 'יש להזין שם איש קשר'
    if (value.trim().length < 2) return 'שם איש הקשר חייב להכיל לפחות 2 תווים'
    return ''
  },
  phone: (value) => {
    if (!value || !value.trim()) return 'יש להזין מספר נייד'
    const cleaned = value.replace(/\D/g, '')
    // Israeli mobile: 05X-XXXXXXX (10 digits) or with country code 972
    if (!/^(05\d{8}|9725\d{8})$/.test(cleaned)) {
      return 'יש להזין מספר נייד תקין (לדוגמה: 0501234567)'
    }
    return ''
  },
  email: (value) => {
    if (!value || !value.trim()) return 'יש להזין כתובת אימייל'
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(value.trim())) return 'יש להזין כתובת אימייל תקינה'
    return ''
  },
  supply: (_value, context) => {
    // If restrictions toggle is off, no validation needed (empty string is valid)
    if (!context?.hasDeliveryRestrictions) return ''
    // If restrictions are on, must select at least one day and one time slot
    if (!context?.selectedDays?.length) return 'יש לבחור לפחות יום אחד'
    if (!context?.selectedTimeSlots?.length) return 'יש לבחור לפחות טווח שעות אחד'
    return ''
  },
  tos: () => '', // TOS doesn't need validation, it's always valid when clicked
}

const validateCurrentStep = () => {
  const key = currentStepData.value.key
  const value = formData.value[key]
  const validator = validators[key]

  if (validator) {
    // Pass context for supply / address validation
    let context = null
    if (key === 'supply') {
      context = {
        hasDeliveryRestrictions: hasDeliveryRestrictions.value,
        selectedDays: selectedDays.value,
        selectedTimeSlots: selectedTimeSlots.value,
      }
    } else if (key === 'address') {
      context = {
        selectedStreet: selectedStreet.value,
        houseNumber: houseNumber.value,
      }
    }
    validationError.value = validator(value, context)
    return validationError.value === ''
  }
  return true
}

// Clear validation error when user types/changes input
watch(
  formData,
  () => {
    if (hasAttemptedSubmit.value && validationError.value) {
      // Re-validate to clear error if input is now valid
      validateCurrentStep()
    }
  },
  { deep: true }
)

// Marketing modal state
const showMarketingModal = ref(false)

// Cities
const cities = ref([])
const cityCodeMap = new Map()
const loadingCities = ref(true)

const getCities = async () => {
  try {
    const list = await fetchCities()
    list.forEach((c) => {
      if (c.code != null && !cityCodeMap.has(c.name)) {
        cityCodeMap.set(c.name, c.code)
      }
    })
    cities.value = list.map((c) => c.name)
  } catch (error) {
    console.error('Error fetching cities:', error)
    cities.value = []
  } finally {
    loadingCities.value = false
  }
}

// Streets
const streets = ref([])
const loadingStreets = ref(false)

const getStreets = async (cityCode) => {
  loadingStreets.value = true
  streets.value = []
  try {
    streets.value = await fetchStreets(cityCode)
  } catch (error) {
    console.error('Error fetching streets:', error)
  } finally {
    loadingStreets.value = false
  }
}

// City selection — the picker handles its own search UI and free-text entry.
const selectCity = (city) => {
  formData.value.city = city
}

// Street + house number. `selectedStreet` may be a picked suggestion or a
// free-text value the user typed.
const selectedStreet = ref(null)
const houseNumber = ref('')

const updateAddress = () => {
  if (selectedStreet.value && houseNumber.value.trim()) {
    formData.value.address = `${selectedStreet.value} ${houseNumber.value.trim()}`
  } else {
    formData.value.address = null
  }
}

const selectStreet = (street) => {
  selectedStreet.value = street
  updateAddress()
}

// Watch city changes to load matching streets (only when the city maps to a
// known code; for a free-text city we simply clear the suggestions).
watch(
  () => formData.value.city,
  (newCity) => {
    const code = newCity ? cityCodeMap.get(newCity) : null
    if (code) {
      getStreets(code)
    } else {
      streets.value = []
    }
    selectedStreet.value = null
    houseNumber.value = ''
    formData.value.address = null
  }
)

// Watch house number to update address
watch(houseNumber, () => {
  updateAddress()
})

// Steps configuration
const steps = [
  {
    key: 'city',
    label: 'עיר ובדיקת אזור חלוקה',
    type: 'select',
    placeholder: 'בחר עיר',
    icon: 'map',
    explainer: 'אם העיר שלך אינה ברשימה - אנו טרם מבצעים משלוחים לאזור שלך.',
  },
  {
    key: 'biz_name',
    label: 'שם העסק',
    type: 'text',
    placeholder: 'הזן את שם העסק',
    icon: 'shop',
    explainer: 'שם העסק שירשם זה הוא שם העסק אשר יוקם בהמשך במערכת.',
  },
  {
    key: 'vat',
    label: 'מספר ח.פ / עוסק מורשה',
    type: 'text',
    placeholder: 'הזן מספר ח.פ',
    icon: 'document',
    explainer: 'רשום את המספר אשר עליו אנו נוציא חשבוניות ברכישות. חשוב לוודא פרטים נכונים.',
  },
  {
    key: 'address',
    label: 'כתובת מלאה',
    type: 'address',
    icon: 'truck',
    explainer: 'כתובת העסק שלכם, היכן שהמשלוחים צפויים להישלח.',
  },
  {
    key: 'contact',
    label: 'שם איש קשר',
    type: 'text',
    placeholder: 'הזן שם איש קשר',
    icon: 'person',
    explainer: 'רשום איך תרצה שנקרא לך במערכת ונפנה אליך.',
  },
  {
    key: 'phone',
    label: 'מספר נייד',
    type: 'tel',
    placeholder: 'הזן מספר נייד',
    icon: 'phone',
    explainer: 'מספר הנייד חשוב מאוד, הוא ישמש אותך בכניסה למערכת ובקבלת קוד.',
  },
  {
    key: 'email',
    label: 'כתובת אימייל',
    type: 'email',
    placeholder: 'הזן כתובת אימייל',
    icon: 'email',
    explainer: 'הזן את כתובת האימייל אליה ישלחו החשבוניות, חדשות ועדכונים מהמערכת.',
  },
  {
    key: 'supply',
    label: 'ימים ושעות קבלת סחורה',
    type: 'supply',
    icon: 'calendar',
    explainer:
      'סמנו אם יש לכם הגבלות בימים או שעות לקבלת סחורה. זה חשוב כדי שנבדוק ונבחן את הקו המתאים אליכם.',
  },
  {
    key: 'tos',
    label: 'אישור תנאי שימוש',
    type: 'tos',
    icon: 'document',
    buttonText: 'מסכימ/ה',
  },
]

const currentStep = ref(0)
const isSubmitting = ref(false)
const isSubmitted = ref(false)
const direction = ref('forward')

const currentStepData = computed(() => steps[currentStep.value])
const isFirstStep = computed(() => currentStep.value === 0)
const isLastStep = computed(() => currentStep.value === steps.length - 1)
const progress = computed(() => ((currentStep.value + 1) / steps.length) * 100)

const canProceed = computed(() => {
  // TOS step is always ready (button click will set the value)
  if (currentStepData.value.type === 'tos') return true

  // Supply step: always ready (either no restrictions, or user selected days/times)
  if (currentStepData.value.type === 'supply') {
    if (!hasDeliveryRestrictions.value) return true
    return selectedDays.value.length > 0 && selectedTimeSlots.value.length > 0
  }

  // Address step: need both street and house number
  if (currentStepData.value.type === 'address') {
    return !!selectedStreet.value && !!houseNumber.value.trim()
  }

  const value = formData.value[currentStepData.value.key]
  return value !== null && value !== ''
})

const nextStep = () => {
  // Validate current step
  hasAttemptedSubmit.value = true
  if (!validateCurrentStep()) {
    return
  }

  // For TOS step, check marketing consent
  if (currentStepData.value.type === 'tos') {
    formData.value.tos = true

    // If marketing not checked, show modal
    if (!formData.value.marketing) {
      showMarketingModal.value = true
      return
    }
  }

  proceedToNextStep()
}

const proceedToNextStep = () => {
  // Clear validation state for next step
  validationError.value = ''
  hasAttemptedSubmit.value = false

  if (isLastStep.value) {
    handleSubmit()
  } else {
    direction.value = 'forward'
    currentStep.value++
  }
}

const closeMarketingModal = () => {
  showMarketingModal.value = false
}

const continueWithoutMarketing = () => {
  showMarketingModal.value = false
  proceedToNextStep()
}

const prevStep = () => {
  if (!isFirstStep.value) {
    // Clear validation state
    validationError.value = ''
    hasAttemptedSubmit.value = false
    direction.value = 'backward'
    currentStep.value--
  }
}

const handleSubmit = async () => {
  isSubmitting.value = true

  try {
    // Save to Firebase
    const result = await submitB2BForm(formData.value)

    if (result.success) {
      // Send email notification
      const adminUrl = `${window.location.origin}/app/admin/b2b-forms/${result.docId}`
      const now = new Date()
      const dateStr = now.toLocaleDateString('he-IL')
      const timeStr = now.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })

      const emailHtml = `
<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>בקשת הצטרפות חדשה</title>
</head>
<body dir="rtl" style="margin: 0; padding: 0; background-color: #f1f5f9; font-family: Arial, Tahoma, sans-serif; direction: rtl; text-align: right;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f1f5f9; padding: 20px 0;" dir="rtl">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="max-width: 600px; width: 100%;" dir="rtl">

          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%); border-radius: 16px 16px 0 0; padding: 32px 24px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 26px; font-weight: bold; direction: rtl;">בקשת הצטרפות חדשה</h1>
              <p style="color: rgba(255,255,255,0.9); margin: 12px 0 0 0; font-size: 15px; direction: rtl;">התקבל טופס הצטרפות לרשת GoFood</p>
            </td>
          </tr>

          <!-- Content -->
          <tr>
            <td style="background: #ffffff; padding: 32px 24px; border-radius: 0 0 16px 16px;">

              <!-- Date Badge -->
              <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom: 24px;">
                <tr>
                  <td align="center">
                    <span style="display: inline-block; background: #e2e8f0; padding: 10px 20px; border-radius: 24px; font-size: 14px; color: #475569; font-weight: 500;">
                      ${dateStr} | ${timeStr}
                    </span>
                  </td>
                </tr>
              </table>

              <!-- Business Details Section -->
              <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom: 24px; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden;" dir="rtl">
                <tr>
                  <td colspan="2" style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); padding: 14px 16px; font-weight: bold; font-size: 15px; color: #1e293b; border-bottom: 1px solid #e2e8f0; text-align: right;">
                    פרטי עסק
                  </td>
                </tr>
                <tr>
                  <td style="padding: 14px 16px; color: #64748b; font-size: 14px; width: 35%; border-bottom: 1px solid #f1f5f9; text-align: right; vertical-align: top;">שם העסק</td>
                  <td style="padding: 14px 16px; color: #1e293b; font-size: 14px; font-weight: 600; border-bottom: 1px solid #f1f5f9; text-align: right;">${formData.value.biz_name}</td>
                </tr>
                <tr>
                  <td style="padding: 14px 16px; color: #64748b; font-size: 14px; border-bottom: 1px solid #f1f5f9; text-align: right; vertical-align: top;">ח.פ / עוסק מורשה</td>
                  <td style="padding: 14px 16px; color: #1e293b; font-size: 14px; border-bottom: 1px solid #f1f5f9; text-align: right;">${formData.value.vat}</td>
                </tr>
                <tr>
                  <td style="padding: 14px 16px; color: #64748b; font-size: 14px; border-bottom: 1px solid #f1f5f9; text-align: right; vertical-align: top;">עיר</td>
                  <td style="padding: 14px 16px; color: #1e293b; font-size: 14px; border-bottom: 1px solid #f1f5f9; text-align: right;">${formData.value.city}</td>
                </tr>
                <tr>
                  <td style="padding: 14px 16px; color: #64748b; font-size: 14px; text-align: right; vertical-align: top;">כתובת מלאה</td>
                  <td style="padding: 14px 16px; color: #1e293b; font-size: 14px; text-align: right;">${formData.value.address}</td>
                </tr>
              </table>

              <!-- Contact Details Section -->
              <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom: 24px; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden;" dir="rtl">
                <tr>
                  <td colspan="2" style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); padding: 14px 16px; font-weight: bold; font-size: 15px; color: #1e293b; border-bottom: 1px solid #e2e8f0; text-align: right;">
                    פרטי קשר
                  </td>
                </tr>
                <tr>
                  <td style="padding: 14px 16px; color: #64748b; font-size: 14px; width: 35%; border-bottom: 1px solid #f1f5f9; text-align: right; vertical-align: top;">איש קשר</td>
                  <td style="padding: 14px 16px; color: #1e293b; font-size: 14px; font-weight: 600; border-bottom: 1px solid #f1f5f9; text-align: right;">${formData.value.contact}</td>
                </tr>
                <tr>
                  <td style="padding: 14px 16px; color: #64748b; font-size: 14px; border-bottom: 1px solid #f1f5f9; text-align: right; vertical-align: top;">טלפון נייד</td>
                  <td style="padding: 14px 16px; color: #1e293b; font-size: 14px; border-bottom: 1px solid #f1f5f9; text-align: right; direction: ltr; unicode-bidi: embed;">${formData.value.phone}</td>
                </tr>
                <tr>
                  <td style="padding: 14px 16px; color: #64748b; font-size: 14px; text-align: right; vertical-align: top;">אימייל</td>
                  <td style="padding: 14px 16px; color: #1e293b; font-size: 14px; text-align: right;">${formData.value.email}</td>
                </tr>
              </table>

              <!-- Supply Preferences Section -->
              <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom: 24px; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden;" dir="rtl">
                <tr>
                  <td style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); padding: 14px 16px; font-weight: bold; font-size: 15px; color: #1e293b; border-bottom: 1px solid #e2e8f0; text-align: right;">
                    העדפות אספקה
                  </td>
                </tr>
                <tr>
                  <td style="padding: 16px; color: #1e293b; font-size: 14px; line-height: 1.6; text-align: right; white-space: pre-wrap;">${formData.value.supply}</td>
                </tr>
              </table>

              <!-- Consents -->
              <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom: 28px;" dir="rtl">
                <tr>
                  <td style="text-align: right;">
                    <span style="display: inline-block; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 500; margin-left: 8px; background: ${formData.value.tos ? '#dcfce7' : '#fee2e2'}; color: ${formData.value.tos ? '#15803d' : '#dc2626'};">
                      תנאי שימוש: ${formData.value.tos ? 'מאושר' : 'לא מאושר'}
                    </span>
                    <span style="display: inline-block; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 500; background: ${formData.value.marketing ? '#dcfce7' : '#fef3c7'}; color: ${formData.value.marketing ? '#15803d' : '#b45309'};">
                      שיווק: ${formData.value.marketing ? 'מאושר' : 'לא מאושר'}
                    </span>
                  </td>
                </tr>
              </table>

              <!-- CTA Button -->
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td align="center">
                    <a href="${adminUrl}" style="display: inline-block; background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%); color: #ffffff; text-decoration: none; padding: 16px 40px; border-radius: 10px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 14px rgba(34, 197, 94, 0.4);">
                      צפה בליד במערכת
                    </a>
                  </td>
                </tr>
              </table>

            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding: 24px; text-align: center;">
              <p style="margin: 0; color: #94a3b8; font-size: 13px;">GoFood - בחירת הקמעונאים</p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
      `

      // Send email via Cloud Function (bypasses Firestore rules)
      await callFunction('sendB2BJoinNotification', {
        bizName: formData.value.biz_name,
        emailHtml,
      })
    }

    isSubmitted.value = true
  } catch (error) {
    console.error('Form submission error:', error)
    // Still show success — the form was saved to Firestore
    isSubmitted.value = true
  } finally {
    isSubmitting.value = false
  }
}

// ============================================
// Mobile keyboard handling
// ============================================
// On touch devices an on-screen keyboard appears; on desktop it doesn't.
const isTouchDevice = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)')?.matches

// Bring the focused field into the area that stays visible above the keyboard.
const scrollFieldIntoView = (el) => {
  if (!el) return
  const vv = window.visualViewport
  if (vv) {
    const rect = el.getBoundingClientRect()
    const viewTop = vv.offsetTop
    const viewBottom = vv.offsetTop + vv.height
    const margin = 16
    // Already comfortably visible — don't fight the user's scroll position.
    if (rect.top >= viewTop + margin && rect.bottom <= viewBottom - margin) return
    const fieldCenter = rect.top + rect.height / 2
    const visibleCenter = viewTop + vv.height / 2
    window.scrollBy({ top: fieldCenter - visibleCenter, behavior: 'smooth' })
  } else {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

// Reveal a field once the keyboard has had time to open.
const onFieldFocus = (e) => {
  if (!isTouchDevice()) return
  const el = e.target
  setTimeout(() => scrollFieldIntoView(el), 300)
}

// "Go"/Enter on the mobile keyboard → dismiss the keyboard.
const blurOnEnter = (e) => {
  e.target?.blur()
}

let detachKeyboardListener = null

onMounted(() => {
  getCities()
  recordVisit()

  // When the on-screen keyboard opens/closes, the visual viewport resizes.
  // Expose its height as a CSS var (so the page gains scroll room) and keep
  // the focused field visible above the keyboard.
  const vv = window.visualViewport
  if (vv) {
    const onViewportResize = () => {
      const keyboardHeight = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
      document.documentElement.style.setProperty('--join-kb-height', `${keyboardHeight}px`)
      const active = document.activeElement
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
        scrollFieldIntoView(active)
      }
    }
    vv.addEventListener('resize', onViewportResize)
    detachKeyboardListener = () => {
      vv.removeEventListener('resize', onViewportResize)
      document.documentElement.style.removeProperty('--join-kb-height')
    }
  }
})

onUnmounted(() => {
  detachKeyboardListener?.()
})
</script>

<template>
  <div class="join-network">
    <!-- Header -->
    <header class="join-header">
      <div class="header-content">
        <RouterLink :to="{ name: ROUTES.HOME }" class="back-link">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M5 12H19M12 5l7 7-7 7" />
          </svg>
          <span>חזרה</span>
        </RouterLink>
        <div class="logo-wrapper">
          <img src="@/assets/logo/logo_76.png" alt="GoFood" class="logo-img" />
          <span class="logo-text">GoFood</span>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="join-main">
      <div class="form-container">
        <!-- Form Header -->
        <div v-if="!isSubmitting && !isSubmitted" class="form-header">
          <h1 class="form-title">הצטרפו למאות בעלי עסקים</h1>
          <p class="form-highlight">והתחילו לחסוך כבר מההזמנה הראשונה!</p>
          <p class="form-subtitle">במטרה לספק שירות ישיר ואיכותי עד לבית העסק</p>

          <div v-if="isFirstStep" class="form-notice">
            <p>אנו מקבלים הזמנות רק לאחר הצטרפות למועדון הלקוחות העסקיים שלנו.</p>
          </div>

          <div v-if="isFirstStep" class="form-warning">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <span>שימו לב: אין מענה בעניין הצטרפות עסקים חדשים בימים שישי-שבת.</span>
          </div>

          <p class="form-cta">אנא מלאו מספר פרטים בסיסיים ונציגנו יצרו עמכם קשר בהקדם!</p>
        </div>

        <!-- Form Body -->
        <div class="form-body" :class="{ 'full-height': isSubmitting || isSubmitted }">
          <!-- Progress Bar -->
          <div v-if="!isSubmitting && !isSubmitted" class="progress-container">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: progress + '%' }"></div>
            </div>
            <span class="progress-text">{{ currentStep + 1 }} / {{ steps.length }}</span>
          </div>

          <!-- Submitting State -->
          <div v-if="isSubmitting" class="finalizing-state">
            <div class="finalizing-icon">
              <svg class="spinner-large" viewBox="0 0 24 24" width="64" height="64">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="2"
                  fill="none"
                  stroke-dasharray="32"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <h2 class="finalizing-title">מסיים את הרישום...</h2>
            <p class="finalizing-text">אנא המתינו רגע</p>
          </div>

          <!-- Success State -->
          <div v-else-if="isSubmitted" class="success-state">
            <div class="success-icon">
              <svg
                viewBox="0 0 24 24"
                width="64"
                height="64"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h2 class="success-title">הבקשה נשלחה בהצלחה!</h2>
            <p class="success-text">נציגנו יצרו עמכם קשר בהקדם</p>
            <RouterLink :to="{ name: ROUTES.HOME }" class="btn-home"> חזרה לדף הבית </RouterLink>
          </div>

          <!-- Form Steps -->
          <div v-else class="step-container">
            <Transition
              :name="direction === 'forward' ? 'slide-left' : 'slide-right'"
              mode="out-in"
            >
              <div :key="currentStep" class="step-content">
                <!-- Step Label -->
                <label class="step-label">{{ currentStepData.label }}</label>

                <!-- City Picker (search, select a suggestion, or type freely) -->
                <SearchPickerField
                  v-if="currentStepData.type === 'select'"
                  :model-value="formData.city"
                  :options="cities"
                  :loading="loadingCities"
                  :has-error="!!(validationError && hasAttemptedSubmit)"
                  allow-free-text
                  trigger-placeholder="בחרו או הקלידו עיר"
                  title="בחירת עיר"
                  search-placeholder="הקלידו שם עיר..."
                  loading-text="טוען ערים..."
                  no-results-text="לא נמצאו תוצאות"
                  @update:model-value="selectCity"
                />

                <!-- Textarea Input -->
                <textarea
                  v-else-if="currentStepData.type === 'textarea'"
                  v-model="formData[currentStepData.key]"
                  class="form-textarea"
                  :class="{ 'input-error': validationError && hasAttemptedSubmit }"
                  :placeholder="currentStepData.placeholder"
                  rows="3"
                ></textarea>

                <!-- Address Step (Street Autocomplete + House Number) -->
                <div v-else-if="currentStepData.type === 'address'" class="address-content">
                  <!-- Street Picker (search, select a suggestion, or type freely) -->
                  <div class="address-field">
                    <label class="address-field-label">רחוב</label>
                    <SearchPickerField
                      :model-value="selectedStreet"
                      :options="streets"
                      :loading="loadingStreets"
                      :has-error="!!(validationError && hasAttemptedSubmit && !selectedStreet)"
                      allow-free-text
                      trigger-placeholder="בחרו או הקלידו רחוב"
                      title="בחירת רחוב"
                      search-placeholder="הקלידו שם רחוב..."
                      loading-text="טוען רחובות..."
                      no-results-text="לא נמצאו תוצאות"
                      @update:model-value="selectStreet"
                    />
                  </div>

                  <!-- House Number -->
                  <div class="address-field">
                    <label class="address-field-label">מספר בית</label>
                    <input
                      v-model="houseNumber"
                      type="text"
                      class="form-input"
                      :class="{
                        'input-error':
                          validationError &&
                          hasAttemptedSubmit &&
                          selectedStreet &&
                          !houseNumber.trim(),
                      }"
                      placeholder="הזינו מספר בית"
                      @focus="onFieldFocus"
                      @keydown.enter="blurOnEnter"
                    />
                  </div>
                </div>

                <!-- Supply Step (Delivery Days & Hours) -->
                <div v-else-if="currentStepData.type === 'supply'" class="supply-content">
                  <!-- Toggle for restrictions -->
                  <label class="supply-toggle">
                    <span class="toggle-label">יש לי הגבלות בזמני ו/או ימי קבלת סחורה</span>
                    <button
                      type="button"
                      class="toggle-switch"
                      :class="{ active: hasDeliveryRestrictions }"
                      @click="hasDeliveryRestrictions = !hasDeliveryRestrictions"
                      role="switch"
                      :aria-checked="hasDeliveryRestrictions"
                    >
                      <span class="toggle-knob"></span>
                    </button>
                  </label>

                  <!-- Days & Hours Selection (shown when toggle is on) -->
                  <Transition name="supply-expand">
                    <div v-if="hasDeliveryRestrictions" class="supply-options">
                      <!-- Days Selection -->
                      <div class="supply-section">
                        <h4 class="supply-section-title">בחרו ימים:</h4>
                        <div class="supply-days">
                          <button
                            v-for="day in deliveryDays"
                            :key="day.key"
                            type="button"
                            class="day-button"
                            :class="{ selected: selectedDays.includes(day.key) }"
                            @click="toggleDay(day.key)"
                          >
                            {{ day.label }}
                          </button>
                        </div>
                      </div>

                      <!-- Time Slots Selection -->
                      <div class="supply-section">
                        <h4 class="supply-section-title">בחרו טווחי שעות:</h4>
                        <div class="supply-times">
                          <button
                            v-for="slot in deliveryTimeSlots"
                            :key="slot.key"
                            type="button"
                            class="time-button"
                            :class="{ selected: selectedTimeSlots.includes(slot.key) }"
                            @click="toggleTimeSlot(slot.key)"
                          >
                            {{ slot.label }}
                          </button>
                        </div>
                      </div>

                      <!-- Preview of final string -->
                      <div v-if="formData.supply" class="supply-preview">
                        <span class="preview-label">תוצאה:</span>
                        <span class="preview-text">{{ formData.supply }}</span>
                      </div>
                    </div>
                  </Transition>
                </div>

                <!-- TOS Step -->
                <div v-else-if="currentStepData.type === 'tos'" class="tos-content">
                  <p class="tos-text">בעת שליחת הטופס הנכם מאשרים כי קראתם את תקנון האתר.</p>
                  <p class="tos-text">כמו כן, הנכם מאשרים כי אתם בוגרים מעל לגיל 18.</p>
                  <div class="tos-links">
                    <RouterLink :to="{ name: ROUTES.POLICY }" class="tos-link" target="_blank"
                      >תקנון האתר</RouterLink
                    >
                    <RouterLink :to="{ name: ROUTES.PRIVACY }" class="tos-link" target="_blank"
                      >מדיניות פרטיות</RouterLink
                    >
                    <RouterLink
                      :to="{ name: ROUTES.ACCESSIBILITY }"
                      class="tos-link"
                      target="_blank"
                      >הצהרת נגישות</RouterLink
                    >
                  </div>

                  <!-- Marketing Consent Checkbox -->
                  <label class="marketing-checkbox">
                    <input type="checkbox" v-model="formData.marketing" class="marketing-input" />
                    <span class="marketing-checkmark">
                      <svg
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="3"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <span class="marketing-label">אישור קבלת תכנים שיווקיים / פרסומיים</span>
                  </label>
                </div>

                <!-- Text/Tel/Email Input -->
                <input
                  v-else
                  v-model="formData[currentStepData.key]"
                  :type="currentStepData.type"
                  class="form-input"
                  :class="{ 'input-error': validationError && hasAttemptedSubmit }"
                  :placeholder="currentStepData.placeholder"
                  @focus="onFieldFocus"
                  @keydown.enter="blurOnEnter"
                />

                <!-- Validation Error Message -->
                <Transition name="error-fade">
                  <p v-if="validationError && hasAttemptedSubmit" class="error-message">
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 8v4M12 16h.01" />
                    </svg>
                    {{ validationError }}
                  </p>
                </Transition>

                <!-- Explainer Section (not shown for TOS) -->
                <div
                  v-if="currentStepData.type !== 'tos'"
                  class="step-explainer"
                  :class="{
                    compact:
                      currentStepData.type === 'textarea' || currentStepData.type === 'supply',
                  }"
                >
                  <div class="explainer-icon">
                    <!-- Map Icon -->
                    <svg
                      v-if="currentStepData.icon === 'map'"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <!-- Shop Icon -->
                    <svg
                      v-else-if="currentStepData.icon === 'shop'"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                    >
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                    <!-- Document Icon -->
                    <svg
                      v-else-if="currentStepData.icon === 'document'"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                    <!-- Truck Icon -->
                    <svg
                      v-else-if="currentStepData.icon === 'truck'"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                    >
                      <rect x="1" y="3" width="15" height="13" />
                      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                      <circle cx="5.5" cy="18.5" r="2.5" />
                      <circle cx="18.5" cy="18.5" r="2.5" />
                    </svg>
                    <!-- Person Icon -->
                    <svg
                      v-else-if="currentStepData.icon === 'person'"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    <!-- Phone Icon -->
                    <svg
                      v-else-if="currentStepData.icon === 'phone'"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                    >
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                      <line x1="12" y1="18" x2="12.01" y2="18" />
                    </svg>
                    <!-- Email Icon -->
                    <svg
                      v-else-if="currentStepData.icon === 'email'"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                    >
                      <path
                        d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                      />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    <!-- Calendar Icon -->
                    <svg
                      v-else-if="currentStepData.icon === 'calendar'"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </div>
                  <p class="explainer-text">{{ currentStepData.explainer }}</p>
                </div>
              </div>
            </Transition>

            <!-- Navigation -->
            <div class="step-nav">
              <!-- Back Button -->
              <button v-if="!isFirstStep" @click="prevStep" class="btn-back" type="button">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M5 12H19M12 5l7 7-7 7" />
                </svg>
                <span>חזרה</span>
              </button>
              <div v-else></div>

              <!-- Next/Submit Button -->
              <button
                @click="nextStep"
                class="btn-next"
                :class="{ disabled: !canProceed }"
                :disabled="!canProceed || isSubmitting"
                type="button"
              >
                <span v-if="isSubmitting" class="btn-loading">
                  <svg class="spinner" viewBox="0 0 24 24" width="24" height="24">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="3"
                      fill="none"
                      stroke-dasharray="32"
                      stroke-linecap="round"
                    />
                  </svg>
                </span>
                <span v-else>{{
                  currentStepData.buttonText || (isLastStep ? 'שליחה' : 'המשך')
                }}</span>
                <svg
                  v-if="!isSubmitting && !isLastStep && !currentStepData.buttonText"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="join-footer">
      <p>© 2024 GoFood. כל הזכויות שמורות.</p>
    </footer>

    <!-- Marketing Modal -->
    <Transition name="modal-fade">
      <div v-if="showMarketingModal" class="modal-overlay" @click.self="closeMarketingModal">
        <div class="modal-content">
          <div class="modal-icon">
            <svg
              viewBox="0 0 24 24"
              width="32"
              height="32"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </div>
          <h3 class="modal-title">שימו לב!</h3>
          <p class="modal-text">
            שמנו לב שלא סימנת קבלת תכנים שיווקיים - שימו לב, ללא סימון זה אתם לא תקבלו עדכונים על
            מבצעים חדשים, פריטים חדשים, מחירי חיסול ועוד.
          </p>
          <div class="modal-actions">
            <button @click="closeMarketingModal" class="btn-modal-back">חזור</button>
            <button @click="continueWithoutMarketing" class="btn-modal-continue">
              המשך בכל זאת
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/* ============================================
   Mobile First Styles - Light Theme
   ============================================ */
.join-network {
  min-height: 100vh;
  /* Add scroll room equal to the on-screen keyboard height so focused
     fields can be scrolled above the keyboard (see --join-kb-height). */
  min-height: calc(100dvh + var(--join-kb-height, 0px));
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%);
}

/* Header */
.join-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border-color);
  padding: var(--spacing-md) var(--spacing-lg);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 480px;
  margin: 0 auto;
}

.back-link {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: #1e293b;
  text-decoration: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  transition: color var(--transition-fast);
}

.back-link:hover {
  color: #d97706;
}

.back-link svg {
  stroke: currentColor;
}

.logo-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.logo-img {
  width: 32px;
  height: 32px;
  border-radius: var(--border-radius-sm);
}

.logo-text {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
}

/* Main */
.join-main {
  flex: 1;
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: var(--spacing-md);
  min-height: 0;
}

.form-container {
  width: 100%;
  max-width: 480px;
  background: white;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-xl);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Form Header */
.form-header {
  text-align: center;
  padding: var(--spacing-md);
  background: linear-gradient(180deg, rgba(245, 158, 11, 0.08) 0%, transparent 100%);
  border-bottom: 1px solid var(--border-color);
}

.form-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs) 0;
}

.form-highlight {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: #d97706;
  margin: 0 0 var(--spacing-xs) 0;
}

.form-subtitle {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin: 0 0 var(--spacing-md) 0;
}

.form-notice {
  background: rgba(30, 41, 59, 0.05);
  border-radius: var(--border-radius);
  padding: var(--spacing-sm) var(--spacing-md);
  margin-bottom: var(--spacing-sm);
}

.form-notice p {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  margin: 0;
  line-height: 1.5;
}

.form-warning {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  background: rgba(239, 68, 68, 0.08);
  border-radius: var(--border-radius);
  padding: var(--spacing-sm) var(--spacing-md);
  margin-bottom: var(--spacing-md);
}

.form-warning svg {
  flex-shrink: 0;
  stroke: #dc2626;
}

.form-warning span {
  font-size: var(--font-size-xs);
  color: #dc2626;
  font-weight: var(--font-weight-medium);
}

.form-cta {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin: 0;
  font-weight: var(--font-weight-medium);
}

/* Form Body */
.form-body {
  flex: 1;
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Progress Bar */
.progress-container {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-lg);
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: var(--border-color);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  font-weight: var(--font-weight-medium);
  min-width: 40px;
  text-align: left;
}

/* Step Container */
.step-container {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.step-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.step-label {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
  text-align: center;
}

/* Inputs */
.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  font-size: var(--font-size-base);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  background: white;
  color: var(--text-primary);
  transition: all var(--transition-fast);
  text-align: right;
  direction: rtl;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.1);
}

.form-input::placeholder,
.form-textarea::placeholder {
  color: var(--text-muted);
}

/* Address Step */
.address-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.address-field {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.address-field-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
}

.form-textarea {
  resize: none;
  min-height: 100px;
}

/* Input Error State */
.form-input.input-error,
.form-select.input-error,
.form-textarea.input-error {
  border-color: #dc2626;
  background: rgba(220, 38, 38, 0.02);
}

.form-input.input-error:focus,
.form-select.input-error:focus,
.form-textarea.input-error:focus {
  border-color: #dc2626;
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.1);
}

/* Error Message */
.error-message {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(220, 38, 38, 0.08);
  border-radius: var(--border-radius);
  color: #dc2626;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.error-message svg {
  flex-shrink: 0;
  stroke: #dc2626;
}

/* Error Fade Transition */
.error-fade-enter-active,
.error-fade-leave-active {
  transition: all 0.2s ease;
}

.error-fade-enter-from,
.error-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Explainer Section */
.step-explainer {
  margin-top: var(--spacing-lg);
  padding: var(--spacing-md);
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(34, 197, 94, 0.02) 100%);
  border: 1px solid rgba(34, 197, 94, 0.15);
  border-radius: var(--border-radius-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--spacing-sm);
}

.explainer-icon {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border-radius: 50%;
  box-shadow: 0 4px 16px rgba(34, 197, 94, 0.25);
}

.explainer-icon svg {
  width: 24px;
  height: 24px;
  stroke: white;
}

.explainer-text {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  line-height: 1.5;
  margin: 0;
  max-width: 280px;
}

/* Compact explainer for textarea step */
.step-explainer.compact {
  flex-direction: row;
  padding: var(--spacing-sm) var(--spacing-md);
  margin-top: var(--spacing-md);
  text-align: right;
}

.step-explainer.compact .explainer-icon {
  width: 40px;
  height: 40px;
}

.step-explainer.compact .explainer-icon svg {
  width: 20px;
  height: 20px;
}

.step-explainer.compact .explainer-text {
  font-size: var(--font-size-sm);
  max-width: none;
}

/* Supply Content (Delivery Days & Hours) */
.supply-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.supply-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding: var(--spacing-md) var(--spacing-lg);
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(217, 119, 6, 0.04) 100%);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.supply-toggle:hover {
  border-color: rgba(245, 158, 11, 0.4);
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.08) 100%);
}

.toggle-label {
  font-size: var(--font-size-base);
  font-weight: 500;
  color: var(--text-primary);
}

.toggle-switch {
  position: relative;
  width: 52px;
  height: 28px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.toggle-switch.active {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-color: #d97706;
}

.toggle-knob {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 22px;
  height: 22px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all var(--transition-fast);
}

.toggle-switch.active .toggle-knob {
  right: auto;
  left: 2px;
}

.supply-options {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  padding: var(--spacing-lg);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
}

.supply-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.supply-section-title {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--text-secondary);
  margin: 0;
}

.supply-days {
  display: flex;
  gap: var(--spacing-xs);
  flex-wrap: wrap;
}

.day-button {
  min-width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-base);
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.day-button:hover {
  border-color: rgba(245, 158, 11, 0.4);
  background: rgba(245, 158, 11, 0.05);
}

.day-button.selected {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-color: #d97706;
  color: white;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

.supply-times {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.time-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-base);
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.time-button:hover {
  border-color: rgba(245, 158, 11, 0.4);
  background: rgba(245, 158, 11, 0.05);
}

.time-button.selected {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-color: #d97706;
  color: white;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

.supply-preview {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-md);
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(22, 163, 74, 0.04) 100%);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: var(--border-radius-sm);
}

.preview-label {
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: #16a34a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.preview-text {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  line-height: 1.5;
}

/* Supply expand transition */
.supply-expand-enter-active,
.supply-expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.supply-expand-enter-from,
.supply-expand-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Tablet styles for supply */
@media (min-width: 768px) {
  .supply-times {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .time-button {
    flex: 1;
    min-width: 140px;
  }
}

/* TOS Content */
.tos-content {
  text-align: center;
  padding: var(--spacing-md) 0;
}

.tos-text {
  font-size: var(--font-size-base);
  color: var(--text-primary);
  line-height: 1.6;
  margin: 0 0 var(--spacing-sm) 0;
}

.tos-links {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs) var(--spacing-md);
  margin-top: var(--spacing-lg);
}

.tos-link {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: color var(--transition-fast);
}

.tos-link:hover {
  color: #d97706;
}

/* Navigation */
.step-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: var(--spacing-lg);
  gap: var(--spacing-md);
}

.btn-back {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: none;
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: color var(--transition-fast);
}

.btn-back:hover {
  color: var(--text-primary);
}

.btn-next {
  flex: 1;
  max-width: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-xl);
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border: none;
  border-radius: var(--border-radius-lg);
  color: white;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all var(--transition-fast);
  box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);
}

.btn-next:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(34, 197, 94, 0.4);
}

.btn-next:active:not(.disabled) {
  transform: translateY(0);
}

.btn-next.disabled {
  background: var(--border-color);
  color: var(--text-muted);
  cursor: not-allowed;
  box-shadow: none;
}

.btn-loading {
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Full Height State (for submitting/success) */
.form-body.full-height {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

/* Finalizing State */
.finalizing-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--spacing-xl);
}

.finalizing-icon {
  margin-bottom: var(--spacing-lg);
  color: #22c55e;
}

.spinner-large {
  animation: spin 1s linear infinite;
}

.finalizing-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-sm) 0;
}

.finalizing-text {
  font-size: var(--font-size-base);
  color: var(--text-secondary);
  margin: 0;
}

/* Success State */
.success-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--spacing-xl);
}

.success-icon {
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border-radius: 50%;
  margin-bottom: var(--spacing-lg);
}

.success-icon svg {
  stroke: white;
}

.success-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-md) 0;
}

.success-text {
  font-size: var(--font-size-lg);
  color: var(--text-secondary);
  margin: 0 0 var(--spacing-2xl) 0;
}

.btn-home {
  padding: var(--spacing-md) var(--spacing-2xl);
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border-radius: var(--border-radius-lg);
  color: white;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  text-decoration: none;
  transition: all var(--transition-fast);
  box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);
}

.btn-home:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(34, 197, 94, 0.4);
}

/* Slide Transitions */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.25s ease;
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* Footer */
.join-footer {
  text-align: center;
  padding: var(--spacing-lg);
  color: var(--text-muted);
  font-size: var(--font-size-xs);
}

.join-footer p {
  margin: 0;
}

/* ============================================
   Responsive - Keep mobile feel on desktop
   ============================================ */
@media (min-width: 768px) {
  .join-main {
    padding: var(--spacing-lg);
  }

  .form-container {
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.08);
  }

  .form-header {
    padding: var(--spacing-lg);
  }

  .form-title {
    font-size: var(--font-size-xl);
  }

  .form-highlight {
    font-size: var(--font-size-lg);
  }

  .form-body {
    padding: var(--spacing-lg);
  }

  .step-label {
    font-size: var(--font-size-xl);
  }

  .step-explainer {
    flex-direction: row;
    padding: var(--spacing-sm) var(--spacing-md);
    text-align: right;
  }

  .explainer-icon {
    width: 36px;
    height: 36px;
  }

  .explainer-icon svg {
    width: 18px;
    height: 18px;
  }

  .explainer-text {
    font-size: var(--font-size-xs);
    max-width: none;
  }

  .btn-next {
    max-width: 220px;
    padding: var(--spacing-lg) var(--spacing-2xl);
    font-size: var(--font-size-xl);
  }
}

/* ============================================
   Marketing Checkbox
   ============================================ */
.marketing-checkbox {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-xl);
  padding: var(--spacing-md);
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(217, 119, 6, 0.05) 100%);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.marketing-checkbox:hover {
  border-color: rgba(245, 158, 11, 0.4);
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.08) 100%);
}

.marketing-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.marketing-checkmark {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  transition: all var(--transition-fast);
}

.marketing-checkmark svg {
  opacity: 0;
  transform: scale(0.5);
  transition: all var(--transition-fast);
  stroke: white;
}

.marketing-input:checked + .marketing-checkmark {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border-color: #16a34a;
}

.marketing-input:checked + .marketing-checkmark svg {
  opacity: 1;
  transform: scale(1);
}

.marketing-label {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
}

/* ============================================
   Marketing Modal
   ============================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-md);
}

.modal-content {
  background: white;
  border-radius: var(--border-radius-xl);
  padding: var(--spacing-xl);
  max-width: 400px;
  width: 100%;
  text-align: center;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
}

.modal-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto var(--spacing-md);
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-icon svg {
  stroke: #d97706;
}

.modal-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-md) 0;
}

.modal-text {
  font-size: var(--font-size-base);
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0 0 var(--spacing-xl) 0;
}

.modal-actions {
  display: flex;
  gap: var(--spacing-sm);
}

.btn-modal-back {
  flex: 1;
  padding: var(--spacing-md) var(--spacing-lg);
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border: none;
  border-radius: var(--border-radius-lg);
  color: white;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-fast);
  box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);
}

.btn-modal-back:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(34, 197, 94, 0.4);
}

.btn-modal-continue {
  flex: 1;
  padding: var(--spacing-md) var(--spacing-lg);
  background: white;
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  color: var(--text-secondary);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-modal-continue:hover {
  border-color: var(--text-muted);
  color: var(--text-primary);
}

/* Modal Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-active .modal-content,
.modal-fade-leave-active .modal-content {
  transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .modal-content,
.modal-fade-leave-to .modal-content {
  transform: scale(0.9) translateY(20px);
  opacity: 0;
}
</style>
